import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { decrypt } from "Services/crypto_service";
import { Layout } from "Components/Layout/Layout";
import { Footer } from "Components/Footer/Footer";
import { CapacitacionPersonalizada } from "Components/CapacitacionPersonalizada/CapacitacionPersonalizada";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { LearningCard } from "Components/LearningCard/LearningCard";
import { LearningCardSkeleton } from "Components/LearningCardSkeleton/LearningCardSkeleton";
import { CourseTrackingCard } from "Components/CourseTrackingCard/CourseTrackingCard";
import { CourseTrackingCardSkeleton } from "Components/CourseTrackingCardSkeleton/CourseTrackingCardSkeleton";
import { Survey } from "Components/Survey/Survey";
import { InformativeText } from "Components/InformativeText/InformativeText";
import { NotebookIcon } from "Components/Icons/Documents/NotebookIcon";
import { useSessionStorage } from "Hooks/useSessionStorage";
import { useQuery } from "Hooks/useQuery";
import { useMutation } from "Hooks/useMutation";
import { useNotificationTimer } from "Hooks/useNotificationTimer";
import { useProgressCalculation } from "Hooks/useProgressCalculation.jsx";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import {
  getCursoDetalle,
  getContenidoCurso,
  postProgresoCurso,
  asignCurso,
  getEncuestaCurso,
} from "Services/cursos_service";
import { postRespuestaEncuesta } from "Services/encuesta_service";
import {
  crearActa,
  actualizarActa,
  crearActaCliente,
} from "Services/actas_service.jsx";
import {
  ULTIMO_CURSO,
  ULTIMO_CURSO_ESTRUCTURA,
  ESTADOS_ACTA,
  TIPOS_CLIENTE,
  SEGMENTOS,
} from "Constantes/constants";
import { RUTAS } from "rutas";
import { storageService } from "Services/storage_service";
import "./Curso.scss";

export const Curso = () => {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState(0);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showCapacitacionPersonalizada, setShowCapacitacionPersonalizada] =
    useState(false);
  const { idCurso } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = storageService.getItem("user");

  const decryptedDpi = useMemo(() => {
    const dpiParam = searchParams.get("dpi");
    if (!dpiParam) return null;
    try {
      return String(decrypt(dpiParam));
    } catch {
      return null;
    }
  }, [searchParams]);
  const { value: ultimoCurso, setValue: setUltimoCurso } =
    useSessionStorage(ULTIMO_CURSO);
  const { triggerNotificationFetch } = useNotificationTimer();
  const { refetch: refetchCursosPaquetes } = useCursosPaquetes();

  const {
    data: cursoData,
    isLoading,
    refetch: refetchCurso,
  } = useQuery(getCursoDetalle, {
    idUsuario: user?.idUsuario,
    idCurso: idCurso,
  });

  const { calculateMaterialsPercentage, calculateTotalPercentage } =
    useProgressCalculation(cursoData);

  const {
    data: contenidoData,
    isLoading: isLoadingContenido,
    refetch: refetchContenido,
  } = useQuery(getContenidoCurso, {
    idUsuario: user?.idUsuario,
    idCurso: idCurso,
    formatoExcluir: ".encuesta",
  });

  const contentSteps = useMemo(() => {
    if (!contenidoData) return [];

    const parents = contenidoData.filter(
      (item) => item.idContenidoPadre === null
    );

    return parents.map((parent) => {
      const children = contenidoData.filter(
        (item) => item.idContenidoPadre === parent.idContenidoCurso
      );

      const stepItems = [parent, ...children];

      return {
        stepTitle: parent.titulo,
        contentItems: stepItems,
      };
    });
  }, [contenidoData]);

  const { data: encuestaData } = useQuery(
    getEncuestaCurso,
    {
      idEncuesta: cursoData?.encuesta?.idEncuesta,
    },
    Boolean(cursoData?.encuesta?.idEncuesta)
  );

  const { mutate: mutateAsignCurso } = useMutation(asignCurso);
  const { mutate: mutatePostProgresoCurso } = useMutation(postProgresoCurso);
  const { mutate: mutateCrearActa } = useMutation(crearActa);
  const { mutate: mutateActualizarActa } = useMutation(actualizarActa);
  const { mutate: mutateCrearActaCliente } = useMutation(crearActaCliente);
  const { mutate: mutatePostRespuestaEncuesta } = useMutation(
    postRespuestaEncuesta
  );

  const crearActaAnonima = async () => {
    if (Boolean(user?.idUsuario)) return;

    await mutateCrearActa({
      idCurso: parseInt(idCurso),
      idEstadoActa: ESTADOS_ACTA.FINALIZADA.ID,
      idSegmento: SEGMENTOS.CAPACITACION_ANONIMA.ID,
    });
  };

  const handleCloseMaterial = async (materialData) => {
    if (window.screen.orientation.type.startsWith("portrait")) return;

    if (Boolean(user?.idUsuario)) {
      if (materialData.completado) return;

      setIsLoadingProgress(true);
      await handleAuthenticatedProgress(materialData);
      setIsLoadingProgress(false);
    } else {
      handleUnauthenticatedProgress(materialData);
    }
  };

  const handleAuthenticatedProgress = async (materialData) => {
    const progressData = {
      idCurso: parseInt(idCurso),
      idUsuario: user.idUsuario,
      idContenidoCurso: materialData.idContenidoCurso,
      usuarioModificacion: user.usuario,
    };

    await mutatePostProgresoCurso(progressData);
    await refetchContenido();
    const cursoActualizado = await refetchCurso();
    await handleActa(cursoActualizado);

    if (parseInt(ultimoCurso?.idCurso) === parseInt(idCurso)) {
      updateLocalStorageProgress(materialData);
    }
  };

  const handleActa = async (cursoActualizado) => {
    setIsLoadingProgress(false);
    const curso = cursoActualizado || cursoData;
    let idActa = curso?.idActa;

    if (!idActa) {
      const idActaNueva = await mutateCrearActa({
        idUsuario: user.idUsuario,
        idCurso: parseInt(idCurso),
        idEstadoActa: ESTADOS_ACTA.INICIADA.ID,
        usuario: user.usuario,
        idSegmento: Boolean(decryptedDpi)
          ? SEGMENTOS.CAPACITACION_PERSONALIZADA.ID
          : user.segmento,
      });
      idActa = idActaNueva;
    }

    if (!idActa) return;

    const completeProgress = curso?.progreso?.porcentajeTotal || 0;
    if (completeProgress > 0 && completeProgress < 100) {
      await mutateActualizarActa({
        idActa,
        idUsuario: user.idUsuario,
        idCurso: parseInt(idCurso),
        idEstadoActa: ESTADOS_ACTA.EN_CURSO.CODIGO,
        usuario: user.usuario,
      });
    }

    if (completeProgress === 100) {
      await finalizarActaCompleta(idActa);
    }
  };

  const finalizarActaCompleta = async (idActa) => {
    const actaActualizada = await mutateActualizarActa({
      idActa,
      idUsuario: user.idUsuario,
      idCurso: parseInt(idCurso),
      idEstadoActa: ESTADOS_ACTA.FINALIZADA.CODIGO,
      usuario: user.usuario,
    });

    if (actaActualizada) {
      if (searchParams.has("dpi")) {
        searchParams.delete("dpi");
        setSearchParams(searchParams);
      }

      await mutateCrearActaCliente({
        idTipoCliente: TIPOS_CLIENTE.AUTONOMO.ID,
        idActa,
        fud: "",
        dpi: user.usuario,
        usuario: user.usuario,
      });
      triggerNotificationFetch();
      refetchCursosPaquetes();
    }
  };

  const handleUnauthenticatedProgress = (materialData) => {
    if (parseInt(ultimoCurso?.idCurso) === parseInt(idCurso)) {
      updateLocalStorageProgress(materialData);
    } else {
      const progresoData = {
        ...ULTIMO_CURSO_ESTRUCTURA,
        idCurso: parseInt(idCurso),
        materiales:
          contenidoData?.map((material) => ({
            idContenidoCurso: material.idContenidoCurso,
            completado:
              material.idContenidoCurso === materialData.idContenidoCurso,
          })) || [],
        encuesta: {
          idEncuesta: cursoData?.encuesta?.idEncuesta || null,
          idContenidoCurso: cursoData?.encuesta?.idContenidoCurso || null,
          completada: false,
          respuestas: [],
        },
      };

      progresoData.progreso.porcentajeMateriales = calculateMaterialsPercentage(
        progresoData.materiales
      );
      progresoData.progreso.porcentajeTotal = calculateTotalPercentage(
        progresoData.materiales,
        progresoData.encuesta
      );

      setUltimoCurso(progresoData);

      if (progresoData.progreso.porcentajeTotal === 100) {
        crearActaAnonima();
      }
    }
  };

  const updateLocalStorageProgress = (materialData) => {
    const allCourseMaterials =
      contenidoData?.map((material) => {
        const existingMaterial = ultimoCurso?.materiales?.find(
          (m) => m.idContenidoCurso === material.idContenidoCurso
        );
        const isCurrentMaterial =
          material.idContenidoCurso === materialData.idContenidoCurso;

        return {
          idContenidoCurso: material.idContenidoCurso,
          completado:
            isCurrentMaterial || existingMaterial?.completado || false,
        };
      }) || ultimoCurso?.materiales;

    const progresoData = {
      ...ultimoCurso,
      materiales: allCourseMaterials,
      progreso: {
        porcentajeMateriales: calculateMaterialsPercentage(allCourseMaterials),
        porcentajeTotal: calculateTotalPercentage(
          allCourseMaterials,
          ultimoCurso?.encuesta
        ),
      },
    };

    setUltimoCurso(progresoData);

    if (progresoData.progreso.porcentajeTotal === 100) {
      crearActaAnonima();
    }
  };

  const getStepProgress = (stepItems) => {
    const isAuthenticated = Boolean(user?.idUsuario);

    if (isAuthenticated) {
      const mainContentCompletedAt = stepItems?.[0]?.fechaCompletado;
      const courseProgressCompletedAt = cursoData?.progreso?.fechaCompletado;

      if (
        mainContentCompletedAt &&
        courseProgressCompletedAt &&
        mainContentCompletedAt <= courseProgressCompletedAt
      ) {
        return 100;
      }

      const completedItems = stepItems.filter((item) => item.completado).length;
      const totalItems = stepItems.length;
      return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    }

    if (parseInt(ultimoCurso?.idCurso) !== parseInt(idCurso)) return 0;

    const completedItems = stepItems.filter((item) => {
      const material = ultimoCurso?.materiales?.find(
        (m) => m.idContenidoCurso === item.idContenidoCurso
      );
      return material?.completado || false;
    }).length;

    const totalItems = stepItems.length;
    return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  };

  const getSurveyCompletedStatus = () => {
    if (Boolean(user?.idUsuario)) {
      return cursoData?.encuesta?.encuestaCompletada;
    } else {
      return (
        parseInt(ultimoCurso?.idCurso) === parseInt(idCurso) &&
        ultimoCurso?.encuesta?.completada
      );
    }
  };

  const isEncuestaDisabled = () => {
    const isAuthenticated = Boolean(user?.idUsuario);
    const isCurrentCourse =
      parseInt(ultimoCurso?.idCurso) === parseInt(idCurso);

    if (isAuthenticated) {
      return cursoData?.progreso?.porcentajeMateriales !== 100;
    }

    if (!isCurrentCourse) return true;

    return ultimoCurso?.progreso?.porcentajeMateriales !== 100;
  };

  const handleCompleteSurvey = async (answers) => {
    const transformedAnswers = Object.entries(answers).map(
      ([idPregunta, answer]) => ({
        idActa: cursoData?.idActa,
        idUsuario: user?.idUsuario,
        idEncuesta: cursoData?.encuesta?.idEncuesta,
        idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
        idPregunta: parseInt(idPregunta),
        idRespuesta: answer.selectedOption,
        idSegmento: SEGMENTOS.CAPACITACION_AUTONOMA.ID,
        usuario: user?.usuario,
      })
    );

    const progresoActualizado = {
      ...ultimoCurso,
      encuesta: {
        idEncuesta: cursoData?.encuesta?.idEncuesta,
        idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
        completada: true,
        respuestas: transformedAnswers,
      },
      progreso: {
        ...ultimoCurso?.progreso,
        porcentajeTotal: calculateTotalPercentage(
          ultimoCurso?.materiales || [],
          {
            completada: true,
          }
        ),
      },
    };

    setUltimoCurso(progresoActualizado);

    if (Boolean(user?.idUsuario)) {
      setIsLoadingProgress(true);
      await handleAuthenticatedProgress({
        idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
      });
      setIsLoadingProgress(false);

      Promise.all(
        transformedAnswers.map((answer) => mutatePostRespuestaEncuesta(answer))
      );
      setUltimoCurso(null);
      triggerNotificationFetch();
      refetchCursosPaquetes();
    }

    if (progresoActualizado.progreso.porcentajeTotal === 100) {
      await crearActaAnonima();
    }

    setTimeout(() => {
      navigate(RUTAS.DIPLOMA_CURSO(idCurso));
    }, 500);
  };

  const handleBackButton = () => {
    setActiveCard(activeCard - 1);
  };

  const handleActionButton = () => {
    const lastContentStepIndex = contentSteps.length - 1;
    const isOnLastContentStep = activeCard === lastContentStepIndex;
    const courseCompleted = cursoData?.progreso?.porcentajeTotal === 100;

    if (!isOnLastContentStep) {
      setActiveCard(activeCard + 1);
      return;
    }

    if (courseCompleted) {
      navigate(RUTAS.DIPLOMA_CURSO(idCurso));
      return;
    }

    const hasSurvey = Boolean(cursoData?.encuesta?.idEncuesta);
    if (hasSurvey) {
      setActiveCard(contentSteps.length);
      return;
    }

    const isAuthenticated = Boolean(user?.idUsuario);
    if (isAuthenticated) {
      setUltimoCurso(null);
    }

    navigate(RUTAS.DIPLOMA_CURSO(idCurso));
  };

  const getIsLastContent = () => {
    if (!hasLoaded) return false;

    const lastContentStepIndex = contentSteps.length - 1;
    const hasSurvey = Boolean(cursoData?.encuesta?.idEncuesta);
    const courseCompleted = cursoData?.progreso?.porcentajeTotal === 100;

    if (activeCard !== lastContentStepIndex) return false;

    if (courseCompleted) return true;

    return !hasSurvey;
  };

  const getActionButtonDisabled = () => {
    const isAuthenticated = Boolean(user?.idUsuario);
    const lastContentStepIndex = contentSteps.length - 1;

    if (isLoadingProgress || isLoading || isLoadingContenido) return true;

    if (activeCard !== lastContentStepIndex) return false;

    if (isAuthenticated) {
      return cursoData?.progreso?.porcentajeMateriales !== 100;
    }

    if (parseInt(ultimoCurso?.idCurso) !== parseInt(idCurso)) return true;
    return ultimoCurso?.progreso?.porcentajeMateriales !== 100;
  };

  useEffect(() => {
    if (cursoData === null) {
      navigate(RUTAS.PRINCIPAL);
    }
  }, [cursoData, navigate]);

  useEffect(() => {
    if (cursoData && contenidoData && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [cursoData, contenidoData, hasLoaded]);

  useEffect(() => {
    if (decryptedDpi && user?.usuario !== decryptedDpi) {
      const timer = setTimeout(() => {
        setShowCapacitacionPersonalizada(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [decryptedDpi, user?.usuario]);

  useEffect(() => {
    if (decryptedDpi && user?.usuario !== decryptedDpi) {
      const timer = setTimeout(() => {
        setShowCapacitacionPersonalizada(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [decryptedDpi, user?.usuario]);

  useEffect(
    () => {
      if (Boolean(user?.idUsuario) && cursoData && !cursoData?.asignado) {
        const asignarCurso = async () => {
          await mutateAsignCurso({
            idUsuario: user.idUsuario,
            idCurso: parseInt(idCurso),
          });
          triggerNotificationFetch();
          refetchCursosPaquetes();
        };
        asignarCurso();
      }
    },
    [user?.idUsuario, cursoData, idCurso] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (
      parseInt(ultimoCurso?.idCurso) !== parseInt(idCurso) ||
      !contenidoData ||
      Boolean(user?.idUsuario)
    )
      return;

    const activeIds = contenidoData.map((m) => m.idContenidoCurso);
    const existingMaterials = ultimoCurso?.materiales?.filter((m) =>
      activeIds.includes(m.idContenidoCurso)
    );

    const wasCompleted = ultimoCurso.progreso?.porcentajeTotal === 100;
    const savedIds = ultimoCurso?.materiales?.map((m) => m.idContenidoCurso);
    const hasNewIds = activeIds.some((id) => !savedIds.includes(id));
    const hasNewMaterials = wasCompleted && hasNewIds;
    const newSurveyAdded =
      wasCompleted &&
      !ultimoCurso.encuesta?.idEncuesta &&
      cursoData?.encuesta?.idEncuesta;

    if (
      existingMaterials.length !== ultimoCurso?.materiales?.length ||
      hasNewMaterials ||
      newSurveyAdded
    ) {
      const allMaterials = hasNewMaterials
        ? activeIds.map(
            (id) =>
              ultimoCurso?.materiales?.find(
                (m) => m.idContenidoCurso === id
              ) || {
                idContenidoCurso: id,
                completado: false,
              }
          )
        : existingMaterials;

      const encuesta = newSurveyAdded
        ? {
            idEncuesta: cursoData?.encuesta?.idEncuesta,
            idContenidoCurso: cursoData?.encuesta?.idContenidoCurso,
            completada: false,
            respuestas: [],
          }
        : ultimoCurso?.encuesta;

      setUltimoCurso({
        ...ultimoCurso,
        materiales: allMaterials,
        encuesta,
        progreso: {
          porcentajeMateriales: calculateMaterialsPercentage(allMaterials),
          porcentajeTotal: calculateTotalPercentage(allMaterials, encuesta),
        },
      });
    }
  }, [contenidoData, user?.idUsuario]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="curso-container">
        <section className="curso-container__breadcrumb">
          <Breadcrumbs aria-label="breadcrumb" className="text-gris-acero">
            <Link
              className="breadcrumb__default"
              onClick={() => {
                navigate(RUTAS.PRINCIPAL);
              }}
            >
              Inicio
            </Link>
            <Link className="breadcrumb__static">
              {!hasLoaded ? "Cargando..." : cursoData?.titulo}
            </Link>
          </Breadcrumbs>
        </section>
        <section
          className={clsx("curso-container__sidebar", {
            "curso-container__sidebar--hidden":
              hasLoaded && contentSteps.length === 0,
          })}
        >
          {!hasLoaded && <CourseTrackingCardSkeleton />}

          {hasLoaded &&
            contentSteps.map((step, index) => (
              <CourseTrackingCard
                key={`step-${index}`}
                title={step.stepTitle}
                completed={getStepProgress(step.contentItems) === 100}
                active={activeCard === index}
                onClick={() => setActiveCard(index)}
              />
            ))}

          {!hasLoaded && <CourseTrackingCardSkeleton />}
          {hasLoaded &&
            Boolean(cursoData?.encuesta?.idEncuesta) &&
            (cursoData?.progreso?.porcentajeTotal !== 100 ||
              (isLoadingProgress && activeCard === contentSteps.length)) && (
              <CourseTrackingCard
                variant="survey"
                completed={getSurveyCompletedStatus()}
                active={activeCard === contentSteps.length}
                onClick={() => setActiveCard(contentSteps.length)}
                disabled={isEncuestaDisabled()}
              />
            )}
        </section>
        <section
          className={clsx("curso-container__content", {
            "curso-container__content--hidden":
              hasLoaded && contentSteps.length === 0,
          })}
        >
          {!hasLoaded && <LearningCardSkeleton />}

          {hasLoaded &&
            contentSteps.map(
              (step, index) =>
                activeCard === index && (
                  <LearningCard
                    key={`learning-card-${index}`}
                    titulo={step.stepTitle}
                    categoria={cursoData?.categoria}
                    progress={getStepProgress(step.contentItems)}
                    isLoadingProgress={isLoadingProgress || isLoadingContenido}
                    contentItems={step.contentItems}
                    onCloseMaterial={handleCloseMaterial}
                    onBack={handleBackButton}
                    onAction={handleActionButton}
                    isLastContent={getIsLastContent()}
                    actionButtonDisabled={getActionButtonDisabled()}
                    backButtonDisabled={activeCard === 0}
                    isLoading={
                      isLoadingProgress || isLoading || isLoadingContenido
                    }
                  />
                )
            )}

          {hasLoaded && activeCard === contentSteps.length && (
            <Survey
              completed={getSurveyCompletedStatus()}
              onCompleteSurvey={handleCompleteSurvey}
              surveyData={encuestaData}
              isLoading={isLoadingProgress}
            />
          )}
        </section>
        {hasLoaded && contentSteps.length === 0 && (
          <div className="curso-container__empty-state">
            <InformativeText>
              <NotebookIcon />
              Este curso no tiene contenido disponible en este momento.
            </InformativeText>
          </div>
        )}
      </div>
      <Footer />
      <CapacitacionPersonalizada
        open={showCapacitacionPersonalizada}
        onClose={() => setShowCapacitacionPersonalizada(false)}
        decryptedDpi={decryptedDpi}
      />
    </Layout>
  );
};
